import { action, makeObservable, observable } from 'mobx';

const computeResizeKey = (sourceId: string, columnId: string) =>
  `source:${sourceId}col:${columnId}`;

const GALLERY_SIZE_PREFIX = 'gallerySize:';
const SPREADSHEET_DENSITY_PREFIX = 'spreadsheetDensity:';

export type GallerySize = 'small' | 'large';
export type SpreadsheetDensity = 'compact' | 'comfortable';

/**
 * Used to track the resizing of source rows to preserve the source state next render
 */
export class UiSourceResizeStore {
  gallerySizeById: Record<string, GallerySize> = {};
  spreadsheetDensityById: Record<string, SpreadsheetDensity> = {};

  constructor() {
    makeObservable(this, {
      gallerySizeById: observable,
      spreadsheetDensityById: observable,
      setSourceRowSize: action.bound,
      getSourceRowSize: action.bound,
      setGallerySize: action.bound,
      setSpreadsheetDensity: action.bound,
    });
  }

  setSourceRowSize = (sourceId: string, columnId: string, width: number) => {
    window.localStorage.setItem(computeResizeKey(sourceId, columnId), width.toString());
  };

  getSourceRowSize = (sourceId: string, columnId: string) => {
    const widthString = window.localStorage.getItem(computeResizeKey(sourceId, columnId));
    if (widthString) {
      const width = parseInt(widthString, 10);
      if (!isNaN(width)) {
        return width;
      }
    }
    return undefined;
  };

  getGallerySize = (sourceId: string): GallerySize => {
    // Check observable cache first
    if (this.gallerySizeById[sourceId]) {
      return this.gallerySizeById[sourceId];
    }
    // Load from localStorage
    const stored = window.localStorage.getItem(`${GALLERY_SIZE_PREFIX}${sourceId}`);
    if (stored === 'small' || stored === 'large') {
      this.gallerySizeById[sourceId] = stored;
      return stored;
    }
    return 'small';
  };

  setGallerySize = (sourceId: string, size: GallerySize) => {
    this.gallerySizeById[sourceId] = size;
    window.localStorage.setItem(`${GALLERY_SIZE_PREFIX}${sourceId}`, size);
  };

  getSpreadsheetDensity = (sourceId: string): SpreadsheetDensity => {
    if (this.spreadsheetDensityById[sourceId]) {
      return this.spreadsheetDensityById[sourceId];
    }
    const stored = window.localStorage.getItem(`${SPREADSHEET_DENSITY_PREFIX}${sourceId}`);
    if (stored === 'compact' || stored === 'comfortable') {
      this.spreadsheetDensityById[sourceId] = stored;
      return stored;
    }
    return 'comfortable';
  };

  setSpreadsheetDensity = (sourceId: string, density: SpreadsheetDensity) => {
    this.spreadsheetDensityById[sourceId] = density;
    window.localStorage.setItem(`${SPREADSHEET_DENSITY_PREFIX}${sourceId}`, density);
  };
}
